import { Alert, Badge, Button, Card, Drawer, Group, Modal, NumberInput, Select, SimpleGrid, Stack, Table, Text, TextInput, Timeline, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle, IconArrowBack, IconArrowForward, IconPlus, IconRoute } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { http } from "../../api/http";
import { useCan } from "../../auth/user";
import { DataTable } from "../../components/DataTable";
import { ReportButton } from "../../components/ReportButton";
import { fmtDate, fmtNum, StatusBadge } from "../../components/ui";
import { useT } from "../../i18n/script";
import { useItems } from "./DiaryTab";
import type { Orchard } from "./OrchardDetailPage";

interface Lot { id: number; lot_code: string; harvest_date: string; species: string; cultivar?: string; quantity_kg: number; quality_class?: string;
  status: string; phi_ok: boolean | null; sold_kg: number; other_out_kg: number }

function LotModal({ orchard, onClose }: { orchard: Orchard; onClose: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const cls = useItems("klasa_kvaliteta");
  const [v, setV] = useState({ harvest_date: new Date().toISOString().slice(0, 10), cultivar: orchard.cultivar ?? "", quantity_kg: "" as number | string,
    boxes: "" as number | string, quality_class: "I" as string | null, caliber: "", storage_location: "", operator_name: "", workers: "" as number | string });
  const [res, setRes] = useState<{ lot_code: string; phi_ok: boolean; warnings: string[] } | null>(null);
  const m = useMutation({
    mutationFn: () => http.post<{ lot_code: string; phi_ok: boolean; warnings: string[] }>(`/api/v1/diary/orchards/${orchard.id}/lots`, {
      ...v, quantity_kg: Number(v.quantity_kg), boxes: v.boxes === "" ? null : Number(v.boxes), workers: v.workers === "" ? null : Number(v.workers) }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["diary"] }); setRes(r); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  return (
    <Modal opened onClose={onClose} title={t("Berba — nova partija")} size="lg">
      {res ? (
        <Stack>
          <Alert color={res.phi_ok ? "green" : "red"} icon={res.phi_ok ? undefined : <IconAlertTriangle />}
            title={`${t("Partija")} ${res.lot_code}`}>
            {res.phi_ok ? t("Karenca svih tretmana je istekla prije berbe.") : t("PAŽNJA: berba prije isteka karence!")}
            {res.warnings.map((w) => <Text key={w} size="sm">• {w}</Text>)}
          </Alert>
          <Group justify="flex-end"><Button onClick={onClose}>{t("Zatvori")}</Button></Group>
        </Stack>
      ) : (
        <Stack>
          <SimpleGrid cols={2}>
            <TextInput type="date" label={t("Datum berbe")} required value={v.harvest_date} onChange={(e) => setV({ ...v, harvest_date: e.currentTarget.value })} />
            <TextInput label={t("Sorta")} value={v.cultivar} onChange={(e) => setV({ ...v, cultivar: e.currentTarget.value })} />
            <NumberInput label={t("Količina (kg)")} required decimalSeparator="," value={v.quantity_kg} onChange={(x) => setV({ ...v, quantity_kg: x })} />
            <NumberInput label={t("Broj ambalaže")} value={v.boxes} onChange={(x) => setV({ ...v, boxes: x })} />
            <Select label={t("Klasa")} data={(cls.data ?? []).map((x) => ({ value: x.code, label: x.name }))} value={v.quality_class} onChange={(x) => setV({ ...v, quality_class: x })} />
            <TextInput label={t("Kalibar")} value={v.caliber} onChange={(e) => setV({ ...v, caliber: e.currentTarget.value })} />
            <TextInput label={t("Skladište")} value={v.storage_location} onChange={(e) => setV({ ...v, storage_location: e.currentTarget.value })} />
            <TextInput label={t("Odgovorno lice")} value={v.operator_name} onChange={(e) => setV({ ...v, operator_name: e.currentTarget.value })} />
          </SimpleGrid>
          <Text size="xs" c="dimmed">{t("Sistem automatski provjerava karencu svih tretmana u sezoni i dodjeljuje kod partije (BPG-godina-voćnjak-redni broj).")}</Text>
          <Group justify="flex-end"><Button onClick={() => m.mutate()} disabled={!v.quantity_kg} loading={m.isPending}>{t("Upiši partiju")}</Button></Group>
        </Stack>
      )}
    </Modal>
  );
}

function MovementModal({ lot, onClose }: { lot: Lot; onClose: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const remaining = Number(lot.quantity_kg) - Number(lot.sold_kg) - Number(lot.other_out_kg);
  const [v, setV] = useState({ movement_type: "prodaja" as string | null, movement_date: new Date().toISOString().slice(0, 10),
    quantity_kg: remaining as number | string, partner_name: "", partner_jib: "", partner_entity: "RS" as string | null, invoice_no: "",
    price_km_kg: "" as number | string, note: "" });
  const m = useMutation({
    mutationFn: () => http.post(`/api/v1/diary/lots/${lot.id}/movements`, { ...v, quantity_kg: Number(v.quantity_kg),
      price_km_kg: v.price_km_kg === "" ? null : Number(v.price_km_kg), partner_jib: v.partner_jib || null, invoice_no: v.invoice_no || null,
      partner_name: v.partner_name || null, partner_entity: v.movement_type === "prodaja" ? v.partner_entity : null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["diary"] }); onClose(); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  return (
    <Modal opened onClose={onClose} title={`${t("Kretanje partije")} ${lot.lot_code}`} size="lg">
      <Stack>
        <Text size="sm">{t("Na stanju")}: <b>{fmtNum(remaining, 1)} kg</b></Text>
        <SimpleGrid cols={2}>
          <Select label={t("Vrsta")} data={[{ value: "prodaja", label: t("Prodaja") }, { value: "prerada", label: t("Prerada") },
            { value: "gubitak", label: t("Gubitak / kalo") }, { value: "skladiste", label: t("Premještanje u skladište") }]}
            value={v.movement_type} onChange={(x) => setV({ ...v, movement_type: x })} />
          <TextInput type="date" label={t("Datum")} value={v.movement_date} onChange={(e) => setV({ ...v, movement_date: e.currentTarget.value })} />
          <NumberInput label={t("Količina (kg)")} decimalSeparator="," max={remaining} value={v.quantity_kg} onChange={(x) => setV({ ...v, quantity_kg: x })} />
          {v.movement_type === "prodaja" && <>
            <NumberInput label={t("Cijena (KM/kg)")} decimalScale={3} decimalSeparator="," value={v.price_km_kg} onChange={(x) => setV({ ...v, price_km_kg: x })} />
            <TextInput label={t("Kupac")} required value={v.partner_name} onChange={(e) => setV({ ...v, partner_name: e.currentTarget.value })} />
            <TextInput label={t("JIB kupca")} value={v.partner_jib} onChange={(e) => setV({ ...v, partner_jib: e.currentTarget.value })} />
            <Select label={t("Entitet / izvoz")} data={["RS", "FBiH", "BD", "izvoz"]} value={v.partner_entity} onChange={(x) => setV({ ...v, partner_entity: x })} />
            <TextInput label={t("Faktura / otkupni blok")} required value={v.invoice_no} onChange={(e) => setV({ ...v, invoice_no: e.currentTarget.value })} />
          </>}
        </SimpleGrid>
        <TextInput label={t("Napomena")} value={v.note} onChange={(e) => setV({ ...v, note: e.currentTarget.value })} />
        <Group justify="flex-end"><Button onClick={() => m.mutate()} loading={m.isPending}>{t("Sačuvaj")}</Button></Group>
      </Stack>
    </Modal>
  );
}

interface Trace {
  lot: Lot & { storage_location?: string }; orchard: { name: string; opstina?: string; cadastral_ref?: string; area_ha: number; organic: boolean; location: number[] };
  producer: string; treatments: Record<string, unknown>[]; fertilization: Record<string, unknown>[]; phenology: { bbch_code: number; obs_date: string }[];
  movements: Record<string, unknown>[]; documents: { id: number; reg_no: string; title: string }[];
  mass_balance: { harvested_kg: number; out_kg: number; remaining_kg: number }; phi_compliant: boolean;
}

export function TraceDrawer({ lotId, onClose }: { lotId: number; onClose: () => void }) {
  const { t } = useT();
  const tr = useQuery({ queryKey: ["diary", "trace", lotId], queryFn: () => http.get<Trace>(`/api/v1/diary/lots/${lotId}/trace`) });
  const x = tr.data;
  return (
    <Drawer opened onClose={onClose} position="right" size="xl" title={<Group><IconRoute size={18} /><Text fw={650}>{t("Sljedivost partije")}</Text></Group>}>
      {x && (
        <Stack>
          <Group justify="space-between">
            <Title order={4}>{x.lot.lot_code}</Title>
            <ReportButton code="sljedivost_partije" params={{ lot_id: lotId }} label="PDF" size="xs" />
          </Group>
          <Group gap="xs">
            <Badge color={x.phi_compliant ? "green" : "red"}>{x.phi_compliant ? t("karenca ispoštovana") : t("berba prije isteka karence")}</Badge>
            <Badge variant="light">{x.lot.species} / {x.lot.cultivar ?? "—"}</Badge>
            {x.orchard.organic && <Badge color="green" variant="outline">{t("organska")}</Badge>}
          </Group>
          <Card withBorder>
            <Text size="sm">{t("Ubrano")}: <b>{fmtNum(x.mass_balance.harvested_kg, 0)} kg</b> · {t("izašlo")}: {fmtNum(x.mass_balance.out_kg, 0)} kg · {t("na stanju")}: <b>{fmtNum(x.mass_balance.remaining_kg, 0)} kg</b></Text>
          </Card>
          <Timeline bulletSize={22}>
            <Timeline.Item bullet={<IconArrowBack size={12} />} title={t("Porijeklo (korak nazad)")}>
              <Text size="sm">{x.orchard.name} · {x.orchard.opstina} · {x.orchard.cadastral_ref ?? "—"} · {fmtNum(x.orchard.area_ha)} ha</Text>
              <Text size="xs" c="dimmed">{x.producer} · {x.orchard.location?.[1]?.toFixed(5)} N, {x.orchard.location?.[0]?.toFixed(5)} E</Text>
            </Timeline.Item>
            <Timeline.Item title={`${t("Tretmani FFS prije berbe")} (${x.treatments.length})`}>
              <Table fz="xs"><Table.Tbody>{x.treatments.map((r, i) => (
                <Table.Tr key={i} c={r.phi_ok ? undefined : "red"}><Table.Td>{fmtDate(r.start_at)}</Table.Td><Table.Td>{String(r.product_name)}</Table.Td>
                  <Table.Td>{fmtNum(r.dose, 3)} {String(r.dose_unit)}</Table.Td><Table.Td>{t("karenca")} {String(r.phi_days ?? "—")} d</Table.Td>
                  <Table.Td>{t("berba od")} {fmtDate(r.earliest_harvest)}</Table.Td></Table.Tr>))}</Table.Tbody></Table>
            </Timeline.Item>
            <Timeline.Item title={`${t("Đubrenje")} (${x.fertilization.length})`}>
              {x.fertilization.map((r, i) => <Text key={i} size="xs">{fmtDate(r.start_at)} · {String(r.product_name)} {fmtNum(r.quantity_per_ha)} {String(r.unit)}</Text>)}
            </Timeline.Item>
            <Timeline.Item title={t("Fenologija")}>
              <Text size="xs">{x.phenology.map((p) => `BBCH ${p.bbch_code}: ${fmtDate(p.obs_date)}`).join(" · ") || "—"}</Text>
            </Timeline.Item>
            <Timeline.Item bullet={<IconArrowForward size={12} />} title={t("Kretanje (korak naprijed)")}>
              <Table fz="xs"><Table.Tbody>{x.movements.map((r, i) => (
                <Table.Tr key={i}><Table.Td>{fmtDate(r.movement_date)}</Table.Td><Table.Td>{String(r.movement_type)}</Table.Td>
                  <Table.Td>{fmtNum(r.quantity_kg, 0)} kg</Table.Td><Table.Td>{String(r.partner_name ?? "")}</Table.Td>
                  <Table.Td>{String(r.partner_entity ?? "")}</Table.Td><Table.Td>{String(r.invoice_no ?? "")}</Table.Td></Table.Tr>))}</Table.Tbody></Table>
            </Timeline.Item>
            {x.documents.length > 0 && <Timeline.Item title={t("Dokumenti")}>
              {x.documents.map((d) => <Text key={d.id} size="xs">{d.reg_no} — {d.title}</Text>)}</Timeline.Item>}
          </Timeline>
        </Stack>
      )}
    </Drawer>
  );
}

export function LotsTab({ orchard, season, openNew }: { orchard: Orchard; season: number; openNew?: { key: string; n: number } | null }) {
  const { t } = useT();
  const can = useCan();
  const [add, setAdd] = useState(false);
  useEffect(() => { if (openNew) setAdd(true); }, [openNew?.n]); // eslint-disable-line react-hooks/exhaustive-deps
  const [mv, setMv] = useState<Lot | null>(null);
  const [trace, setTrace] = useState<number | null>(null);
  const lots = useQuery({ queryKey: ["diary", "lots", orchard.id, season],
    queryFn: () => http.get<Lot[]>("/api/v1/diary/lots", { orchard_id: orchard.id, season }) });
  const rows = lots.data ?? [];
  const tot = rows.reduce((s, l) => s + Number(l.quantity_kg), 0);
  return (
    <Stack>
      <Group justify="space-between">
        <Text size="sm">{t("Ubrano")} {season}: <b>{fmtNum(tot, 0)} kg</b> · {t("prinos")} <b>{fmtNum(tot / 1000 / (orchard.area_ha || 1), 1)} t/ha</b></Text>
        {can("diary:write") && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAdd(true)}>{t("Nova partija (berba)")}</Button>}
      </Group>
      <DataTable rows={rows as unknown as Record<string, unknown>[]} exportName={`partije_${orchard.id}_${season}`} onRowClick={(r) => setTrace(Number(r.id))} columns={[
        { key: "lot_code", label: "Partija", render: (r) => <Text size="sm" ff="monospace">{String(r.lot_code)}</Text> },
        { key: "harvest_date", label: "Datum berbe", type: "date" }, { key: "cultivar", label: "Sorta" }, { key: "quality_class", label: "Klasa" },
        { key: "quantity_kg", label: "Ubrano (kg)", type: "number", decimals: 0 }, { key: "sold_kg", label: "Prodato (kg)", type: "number", decimals: 0 },
        { key: "stanje", label: "Na stanju (kg)", type: "number", render: (r) => fmtNum(Number(r.quantity_kg) - Number(r.sold_kg) - Number(r.other_out_kg), 0) },
        { key: "phi_ok", label: "Karenca", render: (r) => <Badge size="sm" color={r.phi_ok === false ? "red" : "green"}>{r.phi_ok === false ? t("NE") : t("OK")}</Badge> },
        { key: "status", label: "Status", render: (r) => <StatusBadge status={String(r.status)} /> },
        { key: "a", label: "", render: (r) => can("diary:write") ? <Button size="compact-xs" variant="light"
          onClick={(e) => { e.stopPropagation(); setMv(r as unknown as Lot); }}>{t("Prodaja / kretanje")}</Button> : null },
      ]} />
      <Text size="xs" c="dimmed">{t("Klik na partiju otvara sljedivost (korak nazad – korak naprijed).")}</Text>
      {add && <LotModal orchard={orchard} onClose={() => setAdd(false)} />}
      {mv && <MovementModal lot={mv} onClose={() => setMv(null)} />}
      {trace && <TraceDrawer lotId={trace} onClose={() => setTrace(null)} />}
    </Stack>
  );
}
